import React from 'react';

import './PropertyBanner.scss';
import { BareProperty, Property } from '@go-shared/types/property';
import { safeIdCompare, forceIdToString, hashStringToColor } from '@go-shared/lib/utils';
import { Dropdown, DropdownItem, LoadingIndicator } from '@spark-labs/avocado-ui';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { generatePath, RouteComponentProps } from 'react-router';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPropertyInitials } from '@go-client/lib/utils';

interface PropertyBannerPropsBase {
  property?: BareProperty | Property;
  swappable?: boolean;
  pill?: boolean;
}

type PropertyBannerProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'property'> &
  PropertyBannerPropsBase &
  Partial<RouteComponentProps>;

/**
 * Loads the property banner async, it maintains aspect ratio
 * and height, so the app has more UI consistency
 *
 */
export function PropertyBanner(props: PropertyBannerProps) {
  const {
    property,
    pill,
    swappable,
    className: propClassName,
    style: propStyle,
    history,
    location,
    match,
    staticContext,
    ...rest
  } = props;

  const { myProperties } = usePropertyInfo();

  if (!property) return <LoadingIndicator size="small" />;

  const { preferences } = useUserInfo();
  const { favoriteProperties } = preferences;
  const fpSet = new Set(favoriteProperties);

  // possible list of properties include the current property and any
  // other active property
  const filteredProperties = myProperties.filter(
    p => safeIdCompare(p._id, property._id) || p.active
  );

  const sortedProperties = filteredProperties.sort(
    (a, b) =>
      (fpSet.has(forceIdToString(a._id)) ? 0 : 1) - (fpSet.has(forceIdToString(b._id)) ? 0 : 1)
  );

  const bannerInfo = {
    name: property.name,
    initials: getPropertyInitials(property.name),
    bgColor: hashStringToColor(property.name),
  };

  function handleOnPropertySelect(item: DropdownItem) {
    if (!item.key || typeof item.key !== 'string' || !history || !match) return;
    history.push(generatePath(match.path, { property: item.key }));
  }

  const classes = ['property-banner-wrapper'];
  if (pill) classes.push('pill');
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = { minHeight: 40 };
  style.backgroundColor = bannerInfo ? bannerInfo.bgColor : '#f1f1f1';
  style = { ...style, ...propStyle };

  function Banner(children?: React.ReactNode) {
    return (
      <div className="text-white font-semibold px-2-i py-1-i">
        <span>{bannerInfo.name}</span>
        {children}
      </div>
    );
  }

  return (
    <div {...rest} className={classes.join(' ')} style={style}>
      {!swappable || sortedProperties.length < 2 ? (
        Banner()
      ) : (
        <div>
          <Dropdown
            items={sortedProperties.map(p => ({
              key: p._id,
              element: `${fpSet.has(p._id) ? '★ ' : ''}${p.name}`,
            }))}
            onSelect={handleOnPropertySelect}
            className="text-center"
          >
            {({ toggleRef, ...rest2 }) => (
              <BubbleButton
                buttonRef={toggleRef}
                {...rest2}
                variant="white"
                className="p-0-i stretched-link"
                inverse
              >
                {Banner(<FontAwesomeIcon icon="caret-down" className="ml-2" />)}
              </BubbleButton>
            )}
          </Dropdown>
        </div>
      )}
    </div>
  );
}
